/**
 * Pure rules of the New role builder (CT-5613). Permission logic itself (parent on, child off,
 * levels) lives in `utils/permissionTree.js`; this file only decides what the wizard allows.
 */
import { getDescendants, toGrants } from './permissionTree.js';

export const ROLE_BUILDER_STEP = Object.freeze({
  DETAILS: 'details',
  PERMISSIONS: 'permissions',
  MEMBERS: 'members',
});

const normalizeName = (name) => String(name ?? '').trim().toLowerCase();

/** A role with this name already exists (case-insensitive, ignoring surrounding spaces). */
export const isRoleNameTaken = (name, roles) => {
  const wanted = normalizeName(name);
  return wanted !== '' && roles.some((role) => normalizeName(role.name) === wanted);
};

/** The studios a role covers, as the starting studios of a new role (a copy; permissions are chosen in step 2). */
export const studiosOfRole = (role) => [...(role.studios ?? [])];

/** The existing role with this name (case-insensitive, ignoring surrounding spaces), if any. */
export const findRoleByName = (name, roles) => {
  const wanted = normalizeName(name);
  return wanted === '' ? undefined : roles.find((role) => normalizeName(role.name) === wanted);
};

/** The draft's grants limited to the permissions of the selected studios. */
export const grantsForStudios = (draft, studios, studioIds) => {
  const allowed = new Set(
    studios
      .filter((studio) => studioIds.includes(studio.id))
      .flatMap((studio) => studio.tree.flatMap((node) => [node, ...getDescendants(node)]).map((node) => node.id)),
  );
  return toGrants(draft).filter((grant) => allowed.has(grant.permission_id));
};

/**
 * Whether the wizard may leave `step`: Details needs a name that is not taken (by a role we know
 * or by one the server rejected, `conflictName`); Permissions needs at least one granted
 * permission (`grantedCount`); Members is optional.
 *
 * @param {string} step A value of `ROLE_BUILDER_STEP`.
 * @param {{ name: string, grantedCount: number, conflictName?: string|null }} state
 * @param {{ name: string }[]} roles Existing roles.
 */
export const canAdvance = (step, state, roles) => {
  if (step === ROLE_BUILDER_STEP.DETAILS) {
    const name = state.name.trim();
    return name !== '' && !isRoleNameTaken(name, roles) && normalizeName(state.conflictName) !== normalizeName(name);
  }
  if (step === ROLE_BUILDER_STEP.PERMISSIONS) return state.grantedCount > 0;
  return true;
};
